import { useState } from 'react';
import { X, Download } from 'lucide-react';
import { PaletteRecord, db } from '../lib/database';
import { exportFormats } from '../lib/exportFormats';

interface ExportModalProps {
  palette: PaletteRecord;
  onClose: () => void;
  onExport?: (format: string) => void;
}

type ExportFormat =
  | 'json'
  | 'css'
  | 'scss'
  | 'tailwind'
  | 'swift'
  | 'android'
  | 'ase'
  | 'gpl'
  | 'svg'
  | 'act'
  | 'clr'
  | 'swatches'
  | 'gradient-css'
  | 'figma'
  | 'material-tokens'
  | 'rest-payload'
  | 'swiftui'
  | 'jetpack-compose'
  | 'flutter'
  | 'png'
  | 'pdf';

export function ExportModal({ palette, onClose, onExport }: ExportModalProps) {
  const [selectedFormat, setSelectedFormat] = useState<ExportFormat>('json');
  const [preview, setPreview] = useState<string>('');

  const formats: Array<{ value: ExportFormat; label: string; extension: string }> = [
    { value: 'json', label: 'JSON', extension: '.json' },
    { value: 'css', label: 'CSS Variables', extension: '.css' },
    { value: 'scss', label: 'SCSS Variables', extension: '.scss' },
    { value: 'tailwind', label: 'Tailwind Config', extension: '.js' },
    { value: 'swift', label: 'Swift (iOS)', extension: '.swift' },
    { value: 'swiftui', label: 'SwiftUI', extension: '.swift' },
    { value: 'android', label: 'Android XML', extension: '.xml' },
    { value: 'jetpack-compose', label: 'Jetpack Compose', extension: '.kt' },
    { value: 'flutter', label: 'Flutter (Dart)', extension: '.dart' },
    { value: 'ase', label: 'Adobe ASE', extension: '.ase' },
    { value: 'act', label: 'Adobe ACT', extension: '.act' },
    { value: 'gpl', label: 'GIMP Palette', extension: '.gpl' },
    { value: 'clr', label: 'macOS Color', extension: '.clr' },
    { value: 'swatches', label: 'Swatches', extension: '.swatches' },
    { value: 'svg', label: 'SVG Swatches', extension: '.svg' },
    { value: 'gradient-css', label: 'Gradient CSS', extension: '.css' },
    { value: 'figma', label: 'Figma Tokens', extension: '.json' },
    { value: 'material-tokens', label: 'Material Tokens', extension: '.json' },
    { value: 'rest-payload', label: 'REST Payload', extension: '.json' },
    { value: 'png', label: 'PNG Sheet', extension: '.png' },
    { value: 'pdf', label: 'PDF Sheet', extension: '.pdf' },
  ];

  const generatePreview = async (format: ExportFormat): Promise<string> => {
    switch (format) {
      case 'json':
        return exportFormats.exportAsJSON(palette);
      case 'css':
        return await exportFormats.exportAsCSS(palette);
      case 'scss':
        return await exportFormats.exportAsSCSS(palette);
      case 'tailwind':
        return await exportFormats.exportAsTailwindConfig(palette);
      case 'swift':
        return await exportFormats.exportAsSwift(palette);
      case 'swiftui':
        return await exportFormats.exportAsSwiftUI(palette);
      case 'android':
        return await exportFormats.exportAsAndroidXML(palette);
      case 'jetpack-compose':
        return await exportFormats.exportAsJetpackCompose(palette);
      case 'flutter':
        return await exportFormats.exportAsFlutter(palette);
      case 'gpl':
        return await exportFormats.exportAsGPL(palette);
      case 'svg':
        return await exportFormats.exportAsSVG(palette);
      case 'swatches':
        return await exportFormats.exportAsSwatches(palette);
      case 'gradient-css':
        return await exportFormats.exportAsGradientCSS(palette);
      case 'figma':
        return await exportFormats.exportAsFigmaTokens(palette);
      case 'material-tokens':
        return await exportFormats.exportAsMaterialTokens(palette);
      case 'rest-payload':
        return await exportFormats.exportAsRestPayload(palette);
      case 'ase':
      case 'act':
      case 'clr':
      case 'png':
      case 'pdf':
        return 'Binary/Image format - preview not available';
      default:
        return '';
    }
  };

  const handleFormatChange = async (format: ExportFormat) => {
    setSelectedFormat(format);
    setPreview(await generatePreview(format));
  };

  const handleExport = async () => {
    const format = formats.find((f) => f.value === selectedFormat);
    if (!format) return;

    const filename = `${palette.name.replace(/\s+/g, '-').toLowerCase()}${format.extension}`;

    // Handle binary/blob formats
    if (selectedFormat === 'ase') {
      const blob = await exportFormats.exportAsASE(palette);
      exportFormats.downloadFile(blob, filename, 'application/octet-stream');
    } else if (selectedFormat === 'act') {
      const blob = await exportFormats.exportAsACT(palette);
      exportFormats.downloadFile(blob, filename, 'application/octet-stream');
    } else if (selectedFormat === 'clr') {
      const blob = await exportFormats.exportAsCLR(palette);
      exportFormats.downloadFile(blob, filename, 'application/xml');
    } else if (selectedFormat === 'png') {
      const blob = await exportFormats.exportAsPNG(palette);
      exportFormats.downloadFile(blob, filename, 'image/png');
    } else if (selectedFormat === 'pdf') {
      const blob = await exportFormats.exportAsPDF(palette);
      exportFormats.downloadFile(blob, filename, 'application/pdf');
    } else {
      // Handle text formats
      const content = await generatePreview(selectedFormat);
      const mimeTypes: Record<string, string> = {
        json: 'application/json',
        css: 'text/css',
        scss: 'text/x-scss',
        tailwind: 'application/javascript',
        swift: 'text/x-swift',
        swiftui: 'text/x-swift',
        android: 'application/xml',
        'jetpack-compose': 'text/x-kotlin',
        flutter: 'text/x-dart',
        gpl: 'text/plain',
        svg: 'image/svg+xml',
        swatches: 'application/json',
        'gradient-css': 'text/css',
        figma: 'application/json',
        'material-tokens': 'application/json',
        'rest-payload': 'application/json',
      };
      exportFormats.downloadFile(content, filename, mimeTypes[selectedFormat] || 'text/plain');
    }
    
    // Log export activity
    if (palette.id) {
      await db.logActivity({
        type: 'palette_exported',
        paletteId: palette.id,
        paletteName: palette.name,
        description: `Exported palette "${palette.name}" as ${format.label}`,
        details: {
          exportFormat: format.label,
          colorCount: palette.colors.length,
        },
      });
    }
    
    // Notify parent component
    onExport?.(format.label);
  };

  useState(() => {
    void generatePreview(selectedFormat).then(setPreview);
  });

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Export Palette</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-600 dark:text-gray-400" />
          </button>
        </div>

        <div className="p-6 flex-1 overflow-y-auto">
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              Select Format
            </label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {formats.map((format) => (
                <button
                  key={format.value}
                  onClick={() => handleFormatChange(format.value)}
                  className={`px-4 py-3 rounded-lg border-2 transition-all text-sm font-medium ${
                    selectedFormat === format.value
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                      : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 text-gray-700 dark:text-gray-300'
                  }`}
                >
                  {format.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Preview</label>
            <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
              <pre className="text-xs text-gray-800 dark:text-gray-200 overflow-x-auto whitespace-pre-wrap break-words font-mono">
                {preview || generatePreview(selectedFormat)}
              </pre>
            </div>
          </div>
        </div>

        <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-6 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleExport}
            className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center gap-2"
          >
            <Download className="w-4 h-4" />
            Download
          </button>
        </div>
      </div>
    </div>
  );
}
